import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Dialog, EmptyState, SelectField, StatusBadge, LastChanceChips } from '../../ui';
import './selection.css';
import { Input as FcInput } from '../../ui';
import TeamChoice from './TeamChoice';
import {teamAssetKey} from '../../lib/teamAssets';

const entryName = (entry, username) => entry?.entry_label || `${username || 'Entry'} ${entry?.entry_index ?? ''}`;
const formatTime = value => new Date(value).toLocaleString(undefined, {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
});

/** Football composition only. Eligibility, filtering and persistence stay with SelectTeam. */
export function FixtureChoice({ fixture, eligibleTeams, selectedTeam, existingSelections, firstSelection, onChoose, teamCrests = {} }) {
  const choice = (team, opponent, prediction) => {
    // Preserve the controller's existing availability predicate, including its exceptions.
    const selectable = eligibleTeams.includes(team) && selectedTeam !== team;
    const saved = existingSelections.some(selection => selection.selected_team === team);
    const draft = firstSelection?.teamName === team;
    const caption = saved ? 'Current selection' : draft ? '1st pick · Not saved yet' : !selectable ? 'Not eligible for this entry' : null;
    return <div className="fc-fixture-choice__team">
      <TeamChoice name={team} side={prediction==='home_win'?'home':'away'} crest={teamCrests[teamAssetKey(team)]} disabled={!selectable}
        aria-label={`Select ${team} against ${opponent}`}
        onClick={() => onChoose(fixture.id, prediction, team)}/>
      {caption && <span className="fc-helper">{caption}</span>}
    </div>;
  };
  return <li className="fc-fixture-choice">
    {choice(fixture.home_team, fixture.away_team, 'home_win')}
    <div className="fc-fixture-choice__kickoff"><span className="fc-helper">Kickoff</span>
      {fixture.kickoff_time ? <time dateTime={fixture.kickoff_time}>{new Date(fixture.kickoff_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time> : 'Time unavailable'}
    </div>
    {choice(fixture.away_team, fixture.home_team, 'away_win')}
    <p className="fc-helper fc-fixture-choice__league">{fixture.league}</p>
  </li>;
}

function SelectionDialog({ confirmModal, replaceModal, onCancel, onConfirm, onReplace, saving, feedback, entryLabel }) {
  const cancelRef = useRef(null);
  const model = confirmModal || replaceModal;
  const replacing = !confirmModal && Boolean(replaceModal);
  const title = replacing ? 'Replace Existing Selection' : confirmModal?.isDoublePick ? 'Confirm Double-Pick' : 'Confirm Your Selection';
  return <Dialog open={Boolean(model)} title={title} initialFocusRef={cancelRef}
    description={`Entry: ${entryLabel}. ${saving ? 'Saving your selection. Please wait.' : 'Not saved yet.'}`}
    cancelDisabled={saving} onCancel={() => { if (!saving) onCancel(); }}
    actions={<>
      {!replacing && <Button onClick={onConfirm} busy={saving} busyLabel="Saving…">{confirmModal?.isDoublePick ? 'Confirm Both Picks' : 'Yes'}</Button>}
      <Button ref={cancelRef} variant="secondary" disabled={saving} onClick={onCancel}>Cancel</Button>
    </>}>
    <div className="fc-selection-stack" aria-busy={saving || undefined}>
      {replacing ? <>
        <p>You want to select <strong>{replaceModal.newSelection.teamName}</strong>.</p>
        <p>Which existing team do you want to replace?</p>
        {replaceModal.existingSelections.map(selection => <Button key={selection.fixture_id}
          variant="secondary" disabled={saving} onClick={() => onReplace(selection)}>Replace: <strong>{selection.selected_team}</strong></Button>)}
      </> : confirmModal?.isDoublePick ? <>
        <p>You've selected <strong>2 teams</strong>:</p>
        <Alert tone="warning" title="Double-Pick" announce={false}>
          <p>1st Pick: {confirmModal.firstSelection.teamName}</p>
          <p>2nd Pick: {confirmModal.secondSelection.teamName}</p>
          <p>⚠️ BOTH teams must win for you to survive!</p>
        </Alert>
      </> : <p>Are you sure you want to select <strong>{confirmModal?.teamName}</strong>?</p>}
      {saving && <p role="status" className="fc-helper">Saving… Please wait for the result before closing.</p>}
      {feedback?.type === 'error' && <Alert tone="danger" title="Selection not saved">{feedback.message}</Alert>}
    </div>
  </Dialog>;
}

export default function LmsTeamSelection({
  competition, competitionId, entries, entryId, entryStatus, username,
  selectedTeam, selectionDeadline, isLocked, chipsRemaining, chipBalance, usingChip, setUsingChip, eligibleTeams, teamCrests = {},
  mustPickDouble, abandonedOnMatchdays, firstSelection, existingSelections, fixturesByDate,
  confirmModal, replaceModal, feedback, saving, addingEntry,
  onSwitchEntry, onAddEntry, onChoose, onConfirm, onReplace, onCancel,
}) {
  const activeEntry = entries.find(entry => entry.id === entryId);
  const entryLabel = entryName(activeEntry, username);
  const alternative = mustPickDouble && abandonedOnMatchdays.includes(competition.current_match_day);
  // Presentation of the existing flags only; SelectTeam keeps every write guard.
  let choicesTitle = selectedTeam ? 'Team choices' : 'Choose a team to win';
  let choicesGuidance = selectedTeam ? 'Review a different team before confirming a change.' : 'No team saved this matchday. Review your choice before confirming.';
  if (isLocked) {
    choicesTitle = 'Team eligibility';
    choicesGuidance = 'The selection window is closed. Team eligibility below does not reopen it.';
    if (alternative) {
      choicesTitle = 'Choose an alternative team';
      choicesGuidance = 'The normal deadline has passed; the existing abandonment exception allows an alternative for this matchday.';
    } else if (mustPickDouble) {
      choicesTitle = 'Double-pick team choices';
      choicesGuidance = 'The normal deadline has passed; the existing double-pick exception still applies.';
    } else if (usingChip && chipsRemaining > 0) {
      choicesTitle = 'Choose a late selection';
      choicesGuidance = 'Last Chance Chip enabled. Only future fixtures are shown.';
    } else if (!selectedTeam && chipsRemaining > 0) {
      choicesGuidance = 'The normal selection window is closed. Enable the Last Chance Chip above before confirming a late selection.';
    }
  }
  if (['pending', 'rejected', 'eliminated'].includes(entryStatus)) {
    choicesTitle = 'Team eligibility';
    choicesGuidance = 'This entry cannot save a selection. See its status above; team eligibility does not change that status.';
  }
  return <main id="fc-main-content" data-fc-ui className="fc-selection-page" aria-label="LMS team selection">
    <div className="fc-selection-stack fc-selection-content">
      <div className="fc-selection-overview">
      <header className="fc-matchday-header">
        <div className="fc-matchday-header__identity">
        <p className="fc-label">Last Man Standing · Matchday {competition.current_match_day}</p>
        <h1 className="fc-heading-1">{competition.name}</h1>
        </div>
        <div className="fc-matchday-header__details">
        <p className="fc-matchday-header__deadline">Selection deadline<br />
          {selectionDeadline ? <time dateTime={selectionDeadline.toISOString()}>{formatTime(selectionDeadline)}</time> : 'Deadline unavailable'}
        </p>
        <Link className="fc-selection-link" to={`/competition/${competitionId}`}>View Competition</Link>
        </div>
      </header>

      <section className="fc-selection-card fc-selection-entry" aria-label="Your entry">
        {entries.length > 1 ? <SelectField label="Your entry" value={entryId || ''}
          helper={entryLabel.length > 40 ? entryLabel : undefined}
          onChange={event => onSwitchEntry(event.target.value)}>
          {entries.map(entry => <option key={entry.id} value={entry.id}>{entryName(entry, username)}</option>)}
        </SelectField> : <div><p className="fc-label">Your entry</p><p>{activeEntry ? entryLabel : 'No entry available'}</p></div>}
        {competition.status === 'active' && competition.registration_status === 'open' && <Button
          variant="secondary" size="compact" onClick={onAddEntry} busy={addingEntry} busyLabel="Adding…">+ Add another entry</Button>}
        <div className="fc-selection-entry__balance"><LastChanceChips count={chipBalance} compact /></div>
      </section>
      </div>

      {entryStatus === 'pending' && <Alert tone="warning" title="Awaiting approval">An organiser needs to approve this entry before you can select a team.</Alert>}
      {entryStatus === 'rejected' && <Alert tone="danger" title="Entry not approved">This entry was not approved for the competition.</Alert>}
      {entryStatus === 'eliminated' && <Alert tone="danger" title="Entry eliminated">This entry has been eliminated and can no longer make selections.</Alert>}
      {!entryId && <EmptyState title="No entry available">There is no entry to select a team for in this competition.</EmptyState>}

      {isLocked && !selectedTeam && !mustPickDouble && (chipsRemaining > 0 ? <section className="fc-selection-card fc-selection-stack" aria-label="Last Chance Chip">
        <Alert tone="warning" title="⏰ Selection Window Closed" announce={false}>You can use a Last Chance Chip to make a late selection</Alert>
        <p><strong>{chipsRemaining}</strong> Chips Left</p>
        <label className="fc-selection-chip"><FcInput aria-label="Use Last Chance Chip for this selection" type="checkbox" checked={usingChip} onChange={event => setUsingChip(event.target.checked)} />
          <span>Use Last Chance Chip for this selection</span></label>
      </section> : <Alert tone="warning" title="Selection Window Closed">You have no Last Chance Chips remaining</Alert>)}
      {isLocked && selectedTeam && !mustPickDouble && <Alert tone="info" title="Selection Window Closed">You have already made your selection for this matchday</Alert>}

      {alternative && <Alert tone="warning" title="Alternative selection available">Your fixture was abandoned/postponed. You can make an alternative selection for this matchday.</Alert>}
      {mustPickDouble && !alternative && existingSelections.length < 2 && <Alert tone="warning" title="Double-Pick Required">
        <p>Your fixture was abandoned/postponed with no alternatives available. You <strong>MUST select 2 teams</strong> for this matchday - <strong>BOTH must win</strong> for you to survive!</p>
        {firstSelection ? <p><strong>1st Pick: {firstSelection.teamName}</strong> ✓ Now select your 2nd team</p> : <p>Select your first team below ↓</p>}
      </Alert>}

      {existingSelections.length > 0 ? <section className="fc-selection-card fc-selection-stack" aria-label="Current selection">
        <StatusBadge tone="success">Saved selection</StatusBadge>
        <h2 className="fc-heading-2">{selectedTeam}</h2>
        {mustPickDouble && <p>{existingSelections.length === 1 ? '(Need 1 more!)' : '✓ Both selected'}</p>}
      </section> : null}
      {feedback && !confirmModal && !replaceModal && <Alert tone={feedback.type === 'error' ? 'danger' : feedback.type === 'success' ? 'success' : 'info'}
        title={feedback.type === 'error' ? 'Selection not saved' : feedback.type === 'success' ? 'Selection saved' : 'Your selection'}>{feedback.message}</Alert>}

      <section className="fc-selection-stack" aria-labelledby="fc-team-choices">
        <div><h2 id="fc-team-choices" className="fc-heading-2">{choicesTitle}</h2>
          <p className="fc-helper">{choicesGuidance}</p></div>
        {!Object.keys(fixturesByDate).length ? <EmptyState title="No fixtures available for this match day." /> : Object.entries(fixturesByDate).map(([date, fixtures]) =>
          <section key={date} className="fc-selection-stack"><h3 className="fc-heading-3">{date}</h3>
            <ul className="fc-fixture-grid">{fixtures.map(fixture => <FixtureChoice key={fixture.id} {...{ fixture, selectedTeam, existingSelections, firstSelection, eligibleTeams, teamCrests }} onChoose={onChoose} />)}</ul>
          </section>)}
      </section>
      <SelectionDialog {...{ confirmModal, replaceModal, saving, feedback, onCancel, onConfirm, onReplace, entryLabel }} />
    </div>
  </main>;
}
